import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowSquareOut, CheckCircle, FilePdf, XCircle } from '@phosphor-icons/react'
import { useAuth } from '@/auth/context'
import { Banner } from '@/ui'
import { useLabReport, useUnitHolds, type LabReportDetail } from '../../api/lab'
import { ActionButton } from '../../components/Can'
import { ConsolePage, DetailColumns, QueryState } from '../../components/Layout'
import { Code, Facts, RefLink, Section, StatTile, Status, UserName, When } from '../../components/bits'
import { HeldUnitsList } from '../../components/lab/HeldUnits'
import { VerifyReportDialog } from '../../components/lab/VerifyReportDialog'
import { verifyBlockedReason } from '../../model/lab/labReportForm'
import { trimDecimal } from '../../model/lab/labLimitForm'
import { OVERALL_LABEL, REPORT_STATUS_LABEL, SAMPLE_KIND_LABEL, UNIT_TYPE_LABEL, unitLink } from '../../model/lab/labText'
import { humanize } from '../../labels'

/** One lab report (F7.5-f): its rows judged against the limits, the PDF, who entered and checked it. */
export function LabReportDetailPage() {
  const { id } = useParams<{ id: string }>()
  const q = useLabReport(id)
  const r = q.data
  return (
    <ConsolePage
      back={{ to: '/c/lab-reports', label: 'Lab reports' }}
      eyebrow="Lab report"
      title={r ? <Code className="text-[inherit]">{r.reportNo}</Code> : 'Lab report'}
      description={
        r ? (
          <>
            {r.labName ?? 'Lab'} · tested <When at={r.testedAt} dateOnly />
          </>
        ) : undefined
      }
      actions={r ? <Status status={r.status} label={REPORT_STATUS_LABEL[r.status]} /> : undefined}
    >
      <QueryState query={q} label="this report">
        {r && <ReportBody r={r} />}
      </QueryState>
    </ConsolePage>
  )
}

function ReportBody({ r }: { r: LabReportDetail }) {
  const { user } = useAuth()
  const [checking, setChecking] = useState<'VERIFIED' | 'REJECTED' | null>(null)
  const blocked = verifyBlockedReason(user?.id, r)
  const notPass = r.results.filter((x) => x.flag !== 'PASS').length
  const holds = useUnitHolds(r.unitId, notPass > 0)
  const unitTo = unitLink(r.unitType, r.unitId)

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {r.status === 'ENTERED' && (
          <>
            <ActionButton perm="lab-reports:verify" leadingIcon={<CheckCircle />} blockedReason={blocked} onClick={() => setChecking('VERIFIED')}>
              Verify
            </ActionButton>
            <ActionButton perm="lab-reports:verify" variant="secondary" leadingIcon={<XCircle />} blockedReason={blocked} onClick={() => setChecking('REJECTED')}>
              Reject
            </ActionButton>
          </>
        )}
        {r.pdfUrl ? (
          <a
            href={r.pdfUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-10 items-center gap-1.5 rounded-chip border border-ink/80 px-4 text-[12px] font-bold uppercase tracking-label text-ink hover:bg-ink hover:text-paper"
          >
            <FilePdf aria-hidden="true" /> Report PDF <ArrowSquareOut aria-hidden="true" />
          </a>
        ) : (
          <span className="inline-flex h-10 items-center text-[13px] text-muted">The PDF link could not be made right now.</span>
        )}
      </div>
      {r.status === 'ENTERED' && blocked && <p className="-mt-2 text-[13px] text-muted">{blocked}</p>}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatTile
          label="Overall"
          value={r.overall ? (OVERALL_LABEL[r.overall] ?? humanize(r.overall)) : '—'}
          tone={r.overall === 'PASS' ? 'ok' : r.overall === 'FAIL' || r.overall === 'NOT_ACCEPTED' ? 'danger' : 'warn'}
        />
        <StatTile label="Rows" value={r.results.length} />
        <StatTile label="Not pass" value={notPass} tone={notPass > 0 ? 'danger' : 'neutral'} />
        <StatTile label="Check" value={REPORT_STATUS_LABEL[r.status] ?? humanize(r.status)} tone={r.status === 'VERIFIED' ? 'ok' : r.status === 'REJECTED' ? 'danger' : 'warn'} />
      </div>

      {r.overall === 'NOT_ACCEPTED' && (
        <Banner tone="error" title="Not accepted">
          The lab's accreditation was not valid on the test date, so this report cannot back a release.
        </Banner>
      )}
      {r.supersededById && (
        <Banner tone="info" title="Replaced by a newer report">
          <Link to={`/c/lab-reports/${r.supersededById}`} className="underline">
            Open the newer report
          </Link>
        </Banner>
      )}

      <DetailColumns
        main={
          <>
            <Section title="Results" bodyClassName="p-0">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-[14px]">
                  <caption className="sr-only">Results</caption>
                  <thead>
                    <tr className="text-left font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">
                      <th scope="col" className="px-4 py-2">Test</th>
                      <th scope="col" className="px-2 py-2">Substance</th>
                      <th scope="col" className="px-2 py-2">Result</th>
                      <th scope="col" className="px-2 py-2 text-right">LOQ</th>
                      <th scope="col" className="px-2 py-2 text-right">Limit</th>
                      <th scope="col" className="px-4 py-2">Verdict</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.results.map((x) => (
                      <tr key={x.id} className="border-t border-rule/50 align-top">
                        <td className="px-4 py-2">{humanize(x.panel)}</td>
                        <td className="px-2 py-2">
                          {x.analyte}
                          {x.method && <span className="block text-[12px] text-muted">{x.method}</span>}
                        </td>
                        <td className="px-2 py-2">{x.text}</td>
                        <td className="px-2 py-2 text-right font-mono tabular">
                          {x.loq ? `${trimDecimal(x.loq)} ${x.uom}` : '—'}
                        </td>
                        <td className="px-2 py-2 text-right font-mono tabular">
                          {x.limitValue ? `${trimDecimal(x.limitValue)} ${x.uom}` : '—'}
                        </td>
                        <td className="px-4 py-2">
                          <Status status={x.flag} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {r.results.length === 0 && <p className="px-4 py-3 text-[14px] text-muted">No rows.</p>}
              </div>
            </Section>
            {notPass > 0 && (holds.data?.held.length ?? 0) > 0 && (
              <Section title="Held after this result" eyebrow="Lab-fail hold" bodyClassName="p-0">
                <HeldUnitsList holds={holds.data!} />
              </Section>
            )}
          </>
        }
        side={
          <>
            <Section title="Sample">
              <Facts
                columns={2}
                items={[
                  {
                    label: 'Tested',
                    value: unitTo ? <RefLink to={unitTo}>{r.unitCode ?? 'Open'}</RefLink> : <Code>{r.unitCode}</Code>,
                  },
                  { label: 'Kind', value: r.unitType ? (UNIT_TYPE_LABEL[r.unitType] ?? r.unitType) : '—' },
                  { label: 'Sample', value: r.sampleKind ? (SAMPLE_KIND_LABEL[r.sampleKind] ?? r.sampleKind) : '—' },
                  { label: 'Seal', value: r.sealPayload, mono: true },
                ]}
              />
            </Section>
            <Section title="Report">
              <Facts
                columns={2}
                items={[
                  { label: 'Lab', value: r.labName },
                  { label: 'Accreditation', value: r.accreditationNo, mono: true },
                  { label: 'Accredited until', value: <When at={r.accreditationValidTo} dateOnly /> },
                  { label: 'Tested on', value: <When at={r.testedAt} dateOnly /> },
                  { label: 'Received', value: <When at={r.receivedAt} /> },
                  { label: 'Entered by', value: <UserName id={r.enteredById} /> },
                  { label: 'Checked by', value: <UserName id={r.verifiedById} fallback="Not checked yet" /> },
                  { label: 'Checked', value: <When at={r.verifiedAt} /> },
                ]}
              />
              {r.supersedesId && (
                <Link to={`/c/lab-reports/${r.supersedesId}`} className="mt-3 inline-block text-[13px] underline decoration-rule underline-offset-2">
                  The report this one replaces
                </Link>
              )}
              {r.sha256 && (
                <p className="mt-3 text-[12px] text-muted">
                  File fingerprint <Code className="break-all text-[11.5px]">{r.sha256}</Code>
                </p>
              )}
            </Section>
          </>
        }
      />
      {checking && (
        <VerifyReportDialog
          reportId={r.id}
          reportNo={r.reportNo}
          initialDecision={checking}
          blockedReason={blocked}
          onClose={() => setChecking(null)}
        />
      )}
    </>
  )
}
